import { useCallback, useEffect, useState } from 'react';
import type { FilterType, SortType } from '../lib/types';

// Default values
const DEFAULT_FILTER: FilterType = 'all';
const DEFAULT_SORT: SortType = 'date-desc';

// Valid filter and sort values for validation
const VALID_FILTERS: FilterType[] = ['all', 'cheaper', 'active', 'award', 'cash'];
const VALID_SORTS: SortType[] = ['date-desc', 'date-asc', 'savings-desc', 'price-desc', 'price-asc'];

interface UrlFilterState {
  filter: FilterType;
  sort: SortType;
  search: string;
}

function parseUrlParams(): UrlFilterState {
  const params = new URLSearchParams(window.location.search);

  // Parse filter
  const filterParam = params.get('filter');
  const filter = (filterParam && VALID_FILTERS.includes(filterParam as FilterType))
    ? (filterParam as FilterType)
    : DEFAULT_FILTER;

  // Parse sort
  const sortParam = params.get('sort');
  const sort = (sortParam && VALID_SORTS.includes(sortParam as SortType))
    ? (sortParam as SortType)
    : DEFAULT_SORT;

  // Parse search
  const search = params.get('q') || '';

  return { filter, sort, search };
}

function updateUrl(state: UrlFilterState) {
  // Preserve existing query params (e.g., checkout_session_id from Stripe redirects)
  // and only update the filter/sort/search params we own.
  const params = new URLSearchParams(window.location.search);

  // Set or remove filter params based on whether they differ from defaults
  if (state.filter !== DEFAULT_FILTER) {
    params.set('filter', state.filter);
  } else {
    params.delete('filter');
  }
  if (state.sort !== DEFAULT_SORT) {
    params.set('sort', state.sort);
  } else {
    params.delete('sort');
  }
  if (state.search) {
    params.set('q', state.search);
  } else {
    params.delete('q');
  }

  const queryString = params.toString();
  const newUrl = queryString
    ? `${window.location.pathname}?${queryString}`
    : window.location.pathname;

  // Use replaceState to avoid cluttering browser history
  window.history.replaceState(null, '', newUrl);
}

/**
 * Bi-directional sync between URL query params and filter/sort/search state.
 *
 * Only non-default values are written to the URL to keep links clean.
 * Uses replaceState (not pushState) so filter changes don't pollute
 * browser history, while still responding to popstate for back/forward.
 */
export function useUrlFilters() {
  // Initialize state from URL params
  const [state, setState] = useState<UrlFilterState>(() => parseUrlParams());

  // Update URL when state changes
  useEffect(() => {
    updateUrl(state);
  }, [state]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      setState(parseUrlParams());
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Setters that update both state and URL
  const setFilter = useCallback((filter: FilterType) => {
    setState(prev => ({ ...prev, filter }));
  }, []);

  const setSort = useCallback((sort: SortType) => {
    setState(prev => ({ ...prev, sort }));
  }, []);

  const setSearch = useCallback((search: string) => {
    setState(prev => ({ ...prev, search }));
  }, []);

  // Clear all filters (reset to defaults)
  const clearFilters = useCallback(() => {
    setState({ filter: DEFAULT_FILTER, sort: DEFAULT_SORT, search: '' });
  }, []);

  return {
    filter: state.filter,
    sort: state.sort,
    search: state.search,
    setFilter,
    setSort,
    setSearch,
    clearFilters,
  };
}
